import loadable from '@loadable/component';
import { AnimatePresence, AnimateSharedLayout } from 'framer-motion';
import React, { Suspense, useState } from 'react';
import { Redirect, BrowserRouter as Router, Switch } from 'react-router-dom';
import styled, { ThemeProvider } from 'styled-components';
import Gs from './Theme/globalStyles';
import { theme } from './Theme/theme';
import { SnackbarProvider } from "notistack";
import { PrivateRoute } from "./views/private.route";
import { PublicRoute } from "./views/public.route";
import Loader from "./Component/Loader/Loder";
import { MoonPayProvider } from '@moonpay/moonpay-react';
import ThankYouPopup from './Component/Modals/ThankYouPopup';
import { useWelcomePopup } from './Component/Modals/useWelcomePopup';
import WelcomePopup from './Component/Modals/welcomePopUp';

// Define component prop interfaces
interface ComponentProps {
    id?: string;
    name?: string;
    className?: string;
    children?: React.ReactNode;
    style?: React.CSSProperties;
    // Add route-specific props
    location?: {
        pathname: string;
        search: string;
        hash: string;
        // state: any;
    };
    history?: {
        push: (path: string) => void;
        replace: (path: string) => void;
        goBack: () => void;
        goForward: () => void;
        // location: any;
    };
    match?: {
        params: { [key: string]: string | undefined };
        isExact: boolean;
        path: string;
        url: string;
    };
}

interface AuthLayoutProps extends ComponentProps {
    loggedIn: boolean;
}

interface HeaderFooterProps {
    loggedIn: boolean;
}

// Fix: Update ThemeType to match your actual theme structure
interface ThemeType {
    color01: string;
    border01: string;
    bodybg01: string;
    colors?: {
        [key: string]: string;
    };
    fonts?: {
        [key: string]: string;
    };
    // Add other theme properties as needed
}

// Fix 1: Use type assertion to fix component type mismatch
const Header = loadable(
    () => import('./Component/header')
) as React.ComponentType<HeaderFooterProps>;
const Footer = loadable(
    () => import('./Component/footer')
) as unknown as React.ComponentType<HeaderFooterProps>;
const AuthLayout = loadable(
    () => import('./layouts/auth.layout')
) as React.ComponentType<AuthLayoutProps>;
// Update the UserLayout component loading
const UserLayout = loadable(
    () => import('./layouts/user.layout')
) as React.ComponentType<ComponentProps>;

const FlexDiv = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
`;

const DMainContainer = styled(FlexDiv)`
    width: 100%;
    justify-content: flex-start;
    overflow: hidden;
    flex-wrap: wrap;
    position: relative;
    align-content: flex-start;
    flex-direction: column;
`;

const LoaderBX = styled(FlexDiv)`
    width: 100%;
    margin: 50px auto;
    min-height: calc(100vh - 400px);
    display: flex;
    align-items: center;
    justify-content: center;
`;

function App(): JSX.Element {
    const [isDark] = useState<boolean>(true);
    const [loggedIn] = useState<boolean>(
        localStorage.getItem('RwaAuthToken') ? true : false
    );
    const [shouldShowThankYou, setShouldShowThankYou] = useState(false);

    // Welcome popup hook
    const { isPopupOpen, closePopup, closePopupPermanently } =
        useWelcomePopup();

    // Fix 2: Add required properties to match ThemeType
    const themeObj = theme(isDark);
    const selectedTheme: ThemeType = {
        ...themeObj,
        // Add empty colors and fonts objects if they're needed by styled-components
        colors: {},
        fonts: {},
    };

    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');
    if (refCode) {
        sessionStorage.setItem('referralCode', refCode);
    }

    const handleWelcomePopupClose = () => {
        closePopup();
    };

    const handleWelcomePopupSubmit = () => {
        closePopupPermanently();
        setShouldShowThankYou(true);
    };

    const handleThankYouClose = () => {
        setShouldShowThankYou(false);
    };

    return (
        <MoonPayProvider apiKey={process.env.REACT_APP_MOONPAY_PUBLISHABLE_KEY!} debug>
        <Router>
            <SnackbarProvider
                maxSnack={3}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                autoHideDuration={3000}
            >
                <ThemeProvider theme={selectedTheme}>
                    <AnimateSharedLayout>
                        {/* Fix 3: Use type assertion to bypass the children type check */}
                        {React.createElement(
                            AnimatePresence,
                            { exitBeforeEnter: true },
                            <section className='MainBox clearfix'>
                                <Gs.GlobalStyle />
                                <DMainContainer>
                                    <Header loggedIn={loggedIn} />
                                    <Suspense
                                        fallback={
                                            <LoaderBX>
                                                <Loader />
                                            </LoaderBX>
                                        }
                                    >
                                        <Switch>
                                            <PrivateRoute
                                                path='/user'
                                                component={(props) => (
                                                    <UserLayout
                                                        {...(props as ComponentProps)}
                                                    />
                                                )}
                                            />
                                            <PublicRoute
                                                path='/'
                                                component={(props) => (
                                                    <AuthLayout
                                                        {...(props as ComponentProps)}
                                                        loggedIn={loggedIn}
                                                    />
                                                )}
                                            />
                                            {loggedIn ? (
                                                <Redirect to='/user' from='/' />
                                            ) : (
                                                <Redirect from='/user' to='/' />
                                            )}
                                        </Switch>
                                    </Suspense>
                                    <Footer loggedIn={loggedIn} />
                                </DMainContainer>

                                {/* Welcome Popup */}
                                <WelcomePopup
                                    isOpen={isPopupOpen}
                                    onClose={handleWelcomePopupClose}
                                    onSubmit={handleWelcomePopupSubmit}
                                />

                                {/* Thank You Popup */}
                                <ThankYouPopup
                                    isOpen={shouldShowThankYou}
                                    onClose={handleThankYouClose}
                                />
                            </section>
                        )}
                    </AnimateSharedLayout>
                </ThemeProvider>
            </SnackbarProvider>
        </Router>
        </MoonPayProvider>
    );
}

export default App;
